import {
  AUDIO_ASSETS,
  type AudioCategory,
  type AudioCue,
  type AudioSampleId,
  CUE_SAMPLE_IDS,
  ENGINE_SAMPLE_IDS,
} from './audio-manifest'
import { loadAudioSamples } from './sample-library'
import { getEngineSampleMix, getEngineTone } from './vehicle-audio-mix'

export type { AudioCue } from './audio-manifest'
export {
  type EngineSampleMix,
  type EngineTone,
  getEngineSampleMix,
  getEngineTone,
} from './vehicle-audio-mix'

export interface CueTone {
  frequency: number
  duration: number
  gain: number
  type: OscillatorType
}

export interface FireLayer {
  kind: 'transient' | 'body' | 'tail'
  duration: number
  gain: number
  frequency: number
}

const CUE_TONES: Record<AudioCue, CueTone> = {
  fire: { frequency: 760, duration: 0.07, gain: 0.12, type: 'sawtooth' },
  hit: { frequency: 132, duration: 0.16, gain: 0.16, type: 'square' },
  'driver-hit': { frequency: 82, duration: 0.34, gain: 0.18, type: 'sawtooth' },
  'reload-start': { frequency: 240, duration: 0.08, gain: 0.07, type: 'square' },
  'reload-complete': { frequency: 520, duration: 0.1, gain: 0.08, type: 'triangle' },
  'vehicle-contact': { frequency: 74, duration: 0.19, gain: 0.15, type: 'square' },
  'drift-bank': { frequency: 440, duration: 0.22, gain: 0.09, type: 'triangle' },
  boost: { frequency: 230, duration: 0.32, gain: 0.1, type: 'sawtooth' },
  lap: { frequency: 590, duration: 0.36, gain: 0.11, type: 'triangle' },
  finish: { frequency: 740, duration: 0.72, gain: 0.13, type: 'triangle' },
  reset: { frequency: 96, duration: 0.28, gain: 0.12, type: 'square' },
}

const FIRE_LAYERS: readonly FireLayer[] = [
  { kind: 'transient', duration: 0.018, gain: 0.085, frequency: 2_800 },
  { kind: 'body', duration: 0.09, gain: 0.065, frequency: 132 },
  { kind: 'tail', duration: 0.16, gain: 0.025, frequency: 360 },
]

export function getCueTone(cue: AudioCue): CueTone {
  return CUE_TONES[cue]
}

export function getFireLayers(): FireLayer[] {
  return FIRE_LAYERS.map((layer) => ({ ...layer }))
}

export type AudioContextFactory = () => AudioContext | null

export type AudioSampleLoader = (
  context: AudioContext,
  assets: typeof AUDIO_ASSETS,
) => Promise<Map<AudioSampleId, AudioBuffer>>

export interface AudioContextWindow {
  AudioContext?: typeof AudioContext
  webkitAudioContext?: typeof AudioContext
}

export function createBrowserAudioContext(
  browserWindow: AudioContextWindow | undefined = typeof window === 'undefined'
    ? undefined
    : (window as unknown as AudioContextWindow),
): AudioContext | null {
  const Context = browserWindow?.AudioContext ?? browserWindow?.webkitAudioContext
  return Context ? new Context() : null
}

export class TrideAudioEngine {
  private context: AudioContext | null = null
  private master: GainNode | null = null
  private engineGain: GainNode | null = null
  private engineOscillators: OscillatorNode[] = []
  private sampleBuffers = new Map<AudioSampleId, AudioBuffer>()
  private readonly categoryGains = new Map<AudioCategory, GainNode>()
  private readonly loopSources = new Map<AudioSampleId, AudioBufferSourceNode>()
  private readonly loopGains = new Map<AudioSampleId, GainNode>()
  private activeOneShots: AudioBufferSourceNode[] = []
  private readonly variantCursors = new Map<AudioCue, number>()
  private muted = false

  constructor(
    private readonly createContext: AudioContextFactory = createBrowserAudioContext,
    private readonly loadSamples: AudioSampleLoader = loadAudioSamples,
    private readonly random: () => number = Math.random,
  ) {}

  private ensureContext(): AudioContext | null {
    if (this.context) return this.context
    const context = this.createContext()
    if (!context) return null
    this.context = context
    return context
  }

  private resumeContext(context: AudioContext): void {
    if (context.state === 'running' || context.state === 'closed') return
    void context.resume().catch(() => undefined)
  }

  unlock(): boolean {
    try {
      const context = this.ensureContext()
      if (!context) return false
      if (context.state === 'running') return true
      this.resumeContext(context)
      const primer = context.createBufferSource()
      primer.buffer = context.createBuffer(1, 1, context.sampleRate)
      primer.connect(context.destination)
      primer.start()
      return true
    } catch {
      return false
    }
  }

  start(): boolean {
    if (this.master && this.context) {
      this.resumeContext(this.context)
      return true
    }
    try {
      if (!this.unlock()) return false
      const context = this.context
      if (!context) return false
      const master = context.createGain()
      const engineGain = context.createGain()
      const filter = context.createBiquadFilter()
      master.gain.value = this.muted ? 0 : 0.72
      engineGain.gain.value = 0.035
      filter.type = 'lowpass'
      filter.frequency.value = 680

      for (const category of ['engine', 'weapon', 'impact', 'feedback'] as const) {
        const categoryGain = context.createGain()
        categoryGain.gain.value = 1
        categoryGain.connect(master)
        this.categoryGains.set(category, categoryGain)
      }
      const engineCategory = this.categoryGains.get('engine')
      if (!engineCategory) throw new Error('Engine audio bus was not created')
      engineGain.connect(filter)
      filter.connect(engineCategory)
      master.connect(context.destination)
      this.engineOscillators = [
        this.createEngineOscillator(context, engineGain, 'sawtooth', 58),
        this.createEngineOscillator(context, engineGain, 'triangle', 29),
      ]
      this.master = master
      this.engineGain = engineGain
      void this.loadSamples(context, AUDIO_ASSETS)
        .then((buffers) => {
          if (this.context !== context) return
          this.sampleBuffers = buffers
          this.startEngineLoops(context)
        })
        .catch(() => {
          // The synthesized layer stays active when optional loading fails.
        })
      return true
    } catch {
      this.dispose()
      return false
    }
  }

  private createEngineOscillator(
    context: AudioContext,
    destination: AudioNode,
    type: OscillatorType,
    frequency: number,
  ): OscillatorNode {
    const oscillator = context.createOscillator()
    oscillator.type = type
    oscillator.frequency.value = frequency
    oscillator.connect(destination)
    oscillator.start()
    return oscillator
  }

  private startEngineLoops(context: AudioContext): void {
    const destination = this.categoryGains.get('engine')
    if (!destination) return
    const initialMix = getEngineSampleMix(0, false)
    const initialGains: Partial<Record<AudioSampleId, number>> = {
      [ENGINE_SAMPLE_IDS.low]: initialMix.lowGain,
      [ENGINE_SAMPLE_IDS.high]: initialMix.highGain,
      [ENGINE_SAMPLE_IDS.boost]: initialMix.boostGain,
      [ENGINE_SAMPLE_IDS.exhaust]: initialMix.exhaustGain,
      [ENGINE_SAMPLE_IDS.drift]: initialMix.driftGain,
    }

    for (const sampleId of Object.values(ENGINE_SAMPLE_IDS)) {
      const buffer = this.sampleBuffers.get(sampleId)
      if (!buffer || this.loopSources.has(sampleId)) continue
      const source = context.createBufferSource()
      const gain = context.createGain()
      source.buffer = buffer
      source.loop = true
      source.playbackRate.value =
        sampleId === ENGINE_SAMPLE_IDS.low
          ? initialMix.lowRate
          : sampleId === ENGINE_SAMPLE_IDS.high
            ? initialMix.highRate
            : sampleId === ENGINE_SAMPLE_IDS.drift
              ? initialMix.driftRate
              : 1
      gain.gain.value = initialGains[sampleId] ?? AUDIO_ASSETS[sampleId].gain
      source.connect(gain)
      gain.connect(destination)
      source.start()
      this.loopSources.set(sampleId, source)
      this.loopGains.set(sampleId, gain)
    }

    if (this.sampleBuffers.has(ENGINE_SAMPLE_IDS.low) && this.sampleBuffers.has(ENGINE_SAMPLE_IDS.high)) {
      this.engineGain?.gain.setTargetAtTime(0.008, context.currentTime, 0.06)
    }
  }

  update(speed: number, drifting: boolean, boosting: boolean): void {
    if (!this.context || !this.engineGain) return
    const tone = getEngineTone(speed)
    const mix = getEngineSampleMix(speed, boosting, drifting)
    const now = this.context.currentTime
    this.engineOscillators[0]?.frequency.setTargetAtTime(tone.frequency * (boosting ? 1.16 : 1), now, 0.045)
    this.engineOscillators[1]?.frequency.setTargetAtTime(tone.frequency * 0.5, now, 0.06)
    const hasSampleBed = this.loopSources.has(ENGINE_SAMPLE_IDS.low) && this.loopSources.has(ENGINE_SAMPLE_IDS.high)
    const synthGain = (tone.gain + (drifting ? 0.025 : 0)) * (hasSampleBed ? 0.18 : 1)
    this.engineGain.gain.setTargetAtTime(synthGain, now, 0.08)
    this.loopGains.get(ENGINE_SAMPLE_IDS.low)?.gain.setTargetAtTime(mix.lowGain, now, 0.06)
    this.loopGains.get(ENGINE_SAMPLE_IDS.high)?.gain.setTargetAtTime(mix.highGain, now, 0.06)
    this.loopGains.get(ENGINE_SAMPLE_IDS.boost)?.gain.setTargetAtTime(mix.boostGain, now, 0.06)
    this.loopGains.get(ENGINE_SAMPLE_IDS.exhaust)?.gain.setTargetAtTime(mix.exhaustGain, now, 0.08)
    this.loopGains.get(ENGINE_SAMPLE_IDS.drift)?.gain.setTargetAtTime(mix.driftGain, now, 0.05)
    this.loopSources.get(ENGINE_SAMPLE_IDS.low)?.playbackRate.setTargetAtTime(mix.lowRate, now, 0.06)
    this.loopSources.get(ENGINE_SAMPLE_IDS.high)?.playbackRate.setTargetAtTime(mix.highRate, now, 0.06)
    this.loopSources.get(ENGINE_SAMPLE_IDS.drift)?.playbackRate.setTargetAtTime(mix.driftRate, now, 0.06)
  }

  trigger(cue: AudioCue): void {
    if (!this.context || !this.master || this.muted) return
    if (this.triggerSample(cue)) return
    if (cue === 'fire') {
      this.triggerFire(this.context, this.master)
      return
    }
    const tone = getCueTone(cue)
    const now = this.context.currentTime
    const oscillator = this.context.createOscillator()
    const gain = this.context.createGain()
    oscillator.type = tone.type
    oscillator.frequency.setValueAtTime(tone.frequency, now)
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(32, tone.frequency * 0.58), now + tone.duration)
    gain.gain.setValueAtTime(tone.gain, now)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + tone.duration)
    oscillator.connect(gain)
    gain.connect(this.master)
    oscillator.start(now)
    oscillator.stop(now + tone.duration)
  }

  private triggerSample(cue: AudioCue): boolean {
    if (!this.context) return false
    const candidates = CUE_SAMPLE_IDS[cue].filter((id) => this.sampleBuffers.has(id))
    if (candidates.length === 0) return false

    const cursor = this.variantCursors.get(cue) ?? 0
    const sampleId = candidates[cursor % candidates.length]
    if (!sampleId) return false
    const buffer = this.sampleBuffers.get(sampleId)
    const asset = AUDIO_ASSETS[sampleId]
    const destination = this.categoryGains.get(asset.category)
    if (!buffer || !destination) return false

    const source = this.context.createBufferSource()
    const gain = this.context.createGain()
    source.buffer = buffer
    source.playbackRate.value = 0.97 + this.random() * 0.06
    gain.gain.value = asset.gain
    source.connect(gain)
    gain.connect(destination)
    source.onended = () => {
      this.activeOneShots = this.activeOneShots.filter((candidate) => candidate !== source)
    }
    this.activeOneShots.push(source)
    if (this.activeOneShots.length > 12) this.activeOneShots.shift()?.stop()
    this.variantCursors.set(cue, cursor + 1)
    source.start()
    return true
  }

  private triggerFire(context: AudioContext, destination: AudioNode): void {
    const now = context.currentTime
    const [transient, body, tail] = FIRE_LAYERS
    if (!transient || !body || !tail) return

    const buffer = context.createBuffer(1, Math.ceil(context.sampleRate * transient.duration), context.sampleRate)
    const channel = buffer.getChannelData(0)
    for (let index = 0; index < channel.length; index += 1) {
      const decay = 1 - index / channel.length
      channel[index] = (this.random() * 2 - 1) * decay * decay
    }
    const crack = context.createBufferSource()
    const crackFilter = context.createBiquadFilter()
    const crackGain = context.createGain()
    crack.buffer = buffer
    crackFilter.type = 'bandpass'
    crackFilter.frequency.value = transient.frequency
    crackFilter.Q.value = 0.7
    crackGain.gain.setValueAtTime(transient.gain, now)
    crackGain.gain.exponentialRampToValueAtTime(0.0001, now + transient.duration)
    crack.connect(crackFilter)
    crackFilter.connect(crackGain)
    crackGain.connect(destination)
    crack.start(now)
    crack.stop(now + transient.duration)

    this.createFireOscillator(context, destination, body, now, 'square', 0.48)
    this.createFireOscillator(context, destination, tail, now + 0.012, 'triangle', 0.67)
  }

  private createFireOscillator(
    context: AudioContext,
    destination: AudioNode,
    layer: FireLayer,
    start: number,
    type: OscillatorType,
    endPitch: number,
  ): void {
    const oscillator = context.createOscillator()
    const filter = context.createBiquadFilter()
    const gain = context.createGain()
    oscillator.type = type
    oscillator.frequency.setValueAtTime(layer.frequency, start)
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(38, layer.frequency * endPitch), start + layer.duration)
    filter.type = 'lowpass'
    filter.frequency.value = layer.kind === 'body' ? 1_100 : 760
    gain.gain.setValueAtTime(layer.gain, start)
    gain.gain.exponentialRampToValueAtTime(0.0001, start + layer.duration)
    oscillator.connect(filter)
    filter.connect(gain)
    gain.connect(destination)
    oscillator.start(start)
    oscillator.stop(start + layer.duration)
  }

  setMuted(muted: boolean): void {
    this.muted = muted
    if (this.context && this.master) {
      this.master.gain.setTargetAtTime(muted ? 0 : 0.72, this.context.currentTime, 0.04)
    }
  }

  isMuted(): boolean {
    return this.muted
  }

  isStarted(): boolean {
    return this.master !== null
  }

  resetRace(): void {
    for (const source of this.activeOneShots) {
      try {
        source.stop()
      } catch {
        // The one-shot may already have ended.
      }
    }
    this.activeOneShots = []
    this.variantCursors.clear()
  }

  dispose(): void {
    this.resetRace()
    for (const source of this.loopSources.values()) {
      try {
        source.stop()
      } catch {
        // The loop may already be stopped.
      }
    }
    for (const oscillator of this.engineOscillators) {
      try {
        oscillator.stop()
      } catch {
        // The oscillator may already be stopped.
      }
    }
    this.engineOscillators = []
    this.sampleBuffers.clear()
    this.loopSources.clear()
    this.loopGains.clear()
    this.categoryGains.clear()
    this.variantCursors.clear()
    void this.context?.close()
    this.context = null
    this.master = null
    this.engineGain = null
  }
}
